/**
 * PosCart — cart sidebar for the till.
 *
 * Parent owns cart state. This component renders lines, allows editing,
 * lets the operator attach a customer, and surfaces the Tender button.
 */

import { CustomerCombobox } from '@/components/CustomerCombobox';
import { formatPriceMinor } from '@/lib/format';
import type { Customer } from '@/lib/types';
import { Minus, Plus, Trash2, User } from 'lucide-react';
import { useState } from 'react';
import type { CartItem } from './PosTillPage';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface Props {
  cart: CartItem[];
  onUpdateLine: (index: number, patch: Partial<CartItem>) => void;
  onRemoveLine: (index: number) => void;
  onAddCustomLine: () => void;
  customer: { id: string; firstName: string; lastName: string } | null;
  onAttachCustomer: (c: { id: string; firstName: string; lastName: string } | null) => void;
  booking: { id: string; type: string } | null;
  onAttachBooking: (b: { id: string; type: string } | null) => void;
  onTender: () => void;
  currency: string;
}

// ---------------------------------------------------------------------------
// CartLine
// ---------------------------------------------------------------------------

interface CartLineProps {
  line: CartItem;
  index: number;
  currency: string;
  onUpdate: (patch: Partial<CartItem>) => void;
  onRemove: () => void;
}

function CartLine({ line, index, currency, onUpdate, onRemove }: CartLineProps) {
  const lineTotal = line.quantity * line.unitPriceMinor;
  const isManual = line.kind === 'manual';

  return (
    <div
      className="group relative flex flex-col gap-1 border-b border-border/60 px-4 py-3 last:border-0"
      data-testid={`cart-line-${index}`}
    >
      {/* Description row */}
      <div className="flex items-start justify-between gap-2">
        {isManual ? (
          <input
            type="text"
            value={line.description}
            onChange={(e) => onUpdate({ description: e.target.value })}
            placeholder="Item description…"
            className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            aria-label="Line description"
          />
        ) : (
          <span className="min-w-0 flex-1 text-sm font-medium">{line.description}</span>
        )}

        <button
          type="button"
          onClick={onRemove}
          className="shrink-0 rounded-md p-1 text-muted-foreground opacity-0 hover:text-destructive press group-hover:opacity-100"
          aria-label={`Remove ${line.description || 'line'}`}
          data-testid={`remove-line-${index}`}
        >
          <Trash2 className="size-3.5" strokeWidth={1.75} />
        </button>
      </div>

      {/* Qty + price row */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onUpdate({ quantity: Math.max(1, line.quantity - 1) })}
            className="flex size-6 items-center justify-center rounded border border-border text-muted-foreground hover:bg-secondary press"
            aria-label="Decrease quantity"
          >
            <Minus className="size-3" strokeWidth={2} />
          </button>
          <input
            type="number"
            min="1"
            value={line.quantity}
            onChange={(e) => {
              const v = Number.parseInt(e.target.value, 10);
              if (!Number.isNaN(v) && v > 0) onUpdate({ quantity: v });
            }}
            className="w-10 bg-transparent text-center text-sm num outline-none"
            aria-label="Quantity"
            data-testid={`qty-input-${index}`}
          />
          <button
            type="button"
            onClick={() => onUpdate({ quantity: line.quantity + 1 })}
            className="flex size-6 items-center justify-center rounded border border-border text-muted-foreground hover:bg-secondary press"
            aria-label="Increase quantity"
          >
            <Plus className="size-3" strokeWidth={2} />
          </button>
        </div>

        <div className="flex items-center gap-2">
          {isManual && (
            <div className="flex items-center rounded border border-border px-2">
              <span className="text-xs text-muted-foreground">£</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={(line.unitPriceMinor / 100).toFixed(2)}
                onChange={(e) => {
                  const v = Math.round(Number.parseFloat(e.target.value || '0') * 100);
                  if (!Number.isNaN(v)) onUpdate({ unitPriceMinor: v });
                }}
                className="w-16 bg-transparent py-0.5 text-right text-sm num outline-none"
                aria-label="Unit price"
              />
            </div>
          )}
          <span className="num text-sm font-medium" data-testid={`line-total-${index}`}>
            {formatPriceMinor(lineTotal, currency)}
          </span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PosCart
// ---------------------------------------------------------------------------

export function PosCart({
  cart,
  onUpdateLine,
  onRemoveLine,
  onAddCustomLine,
  customer,
  onAttachCustomer,
  booking,
  onAttachBooking,
  onTender,
  currency,
}: Props) {
  const [showCustomerPicker, setShowCustomerPicker] = useState(false);
  const [showBookingInput, setShowBookingInput] = useState(false);
  const [bookingIdInput, setBookingIdInput] = useState('');

  const subtotal = cart.reduce((sum, l) => sum + l.quantity * l.unitPriceMinor, 0);
  const hasPositiveLine = cart.some((l) => l.quantity > 0 && l.unitPriceMinor > 0);

  function handleCustomerSelect(c: Customer | null) {
    onAttachCustomer(c ? { id: c.id, firstName: c.firstName, lastName: c.lastName } : null);
    setShowCustomerPicker(false);
  }

  function handleBookingAttach() {
    const trimmed = bookingIdInput.trim();
    if (trimmed) {
      onAttachBooking({ id: trimmed, type: 'boarding' });
      setShowBookingInput(false);
      setBookingIdInput('');
    }
  }

  return (
    <div className="flex h-full flex-col bg-surface">
      {/* Cart header */}
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h2 className="text-sm font-medium">Cart</h2>
        <span className="num text-xs text-muted-foreground">
          {cart.length} {cart.length === 1 ? 'item' : 'items'}
        </span>
      </div>

      {/* Customer / booking attachments */}
      <div className="border-b border-border px-4 py-2 space-y-2">
        {/* Customer */}
        {customer ? (
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <User className="size-3.5" strokeWidth={1.75} />
              <span>
                {customer.firstName} {customer.lastName}
              </span>
            </div>
            <button
              type="button"
              onClick={() => onAttachCustomer(null)}
              className="text-xs text-muted-foreground hover:text-foreground press"
            >
              Remove
            </button>
          </div>
        ) : showCustomerPicker ? (
          <div>
            <CustomerCombobox value={null} onChange={handleCustomerSelect} />
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowCustomerPicker(true)}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground press"
            data-testid="attach-customer-btn"
          >
            <User className="size-3.5" strokeWidth={1.75} />
            Attach customer
          </button>
        )}

        {/* Booking */}
        {booking ? (
          <div className="flex items-center justify-between text-sm">
            <span className="num text-xs text-muted-foreground">Booking {booking.id}</span>
            <button
              type="button"
              onClick={() => onAttachBooking(null)}
              className="text-xs text-muted-foreground hover:text-foreground press"
            >
              Remove
            </button>
          </div>
        ) : showBookingInput ? (
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={bookingIdInput}
              onChange={(e) => setBookingIdInput(e.target.value)}
              placeholder="Booking ID…"
              className="flex-1 rounded border border-border bg-background px-2 py-1 text-xs outline-none"
              aria-label="Booking ID"
            />
            <button
              type="button"
              onClick={handleBookingAttach}
              className="rounded bg-secondary px-2 py-1 text-xs font-medium press"
            >
              Add
            </button>
            <button
              type="button"
              onClick={() => {
                setShowBookingInput(false);
                setBookingIdInput('');
              }}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowBookingInput(true)}
            className="text-xs text-muted-foreground hover:text-foreground press"
            data-testid="attach-booking-btn"
          >
            Attach booking
          </button>
        )}
      </div>

      {/* Cart lines */}
      <div className="flex-1 overflow-y-auto">
        {cart.length === 0 ? (
          <div className="flex h-full items-center justify-center p-6 text-center">
            <p className="text-sm text-muted-foreground">Click a product to add it to the cart.</p>
          </div>
        ) : (
          <div>
            {cart.map((line, i) => (
              <CartLine
                // biome-ignore lint/suspicious/noArrayIndexKey: cart position is stable per item
                key={i}
                line={line}
                index={i}
                currency={currency}
                onUpdate={(patch) => onUpdateLine(i, patch)}
                onRemove={() => onRemoveLine(i)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Cart footer */}
      <div className="border-t border-border p-4 space-y-3">
        <button
          type="button"
          onClick={onAddCustomLine}
          className="w-full rounded-lg border border-border/60 border-dashed py-2 text-xs text-muted-foreground hover:border-border hover:text-foreground press"
          data-testid="add-custom-line-btn"
        >
          + Add custom line
        </button>

        {/* Totals */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Subtotal</span>
          <span className="num font-medium" data-testid="cart-subtotal">
            {formatPriceMinor(subtotal, currency)}
          </span>
        </div>

        <button
          type="button"
          onClick={onTender}
          disabled={!hasPositiveLine}
          className="w-full rounded-lg bg-primary py-3 text-sm font-medium text-primary-foreground press disabled:cursor-not-allowed disabled:opacity-40"
          data-testid="tender-btn"
        >
          Tender {subtotal > 0 ? formatPriceMinor(subtotal, currency) : ''}
        </button>
      </div>
    </div>
  );
}
